import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { AlarmSilence } from "../../schema";
import { AlarmConfiguration as AlarmConfigurationSchema } from "../../schema";
import { alarmConfigurationQuery } from "../queries";
import {
  deriveView,
  type AlarmControlView,
  type CameraStatus,
  type ConfigurationSource,
  type PendingIntentKind,
} from "../alarm-control";

/**
 * Minute-grained ticker so the view re-derives as transitions occur. Exposed
 * only to the hook — components only ever see `AlarmControlView`.
 */
const useMinuteTick = (): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      clearInterval(id);
    };
  }, []);
  return now;
};

export type UseAlarmControlViewInput = {
  readonly siteId: SiteId;
  readonly assetCameraKey: AssetCameraKey;
  /**
   * Live camera status (whether the siren is currently going). Owners of the
   * camera query (usually a parent component backed by `useCameras` or the
   * a3s-app camera query) pass this in, rather than this hook opening its
   * own live-status subscription.
   */
  readonly cameraStatus: CameraStatus;
  /** Currently-applied silence, used to compute `minutesRemaining`. */
  readonly silence: AlarmSilence | null;
  /**
   * Intent of the in-flight command, if any. Typically wired directly from
   * `useAlarmControlDispatch().pendingIntent`. When non-null, `deriveView`
   * wraps the result in a `pending` variant so the component shows a
   * spinner on the active button.
   */
  readonly pendingIntent?: PendingIntentKind | null;
  /** Overridable for deterministic tests; defaults to a minute ticker. */
  readonly now?: Date;
};

/**
 * Bind the alarm configuration query to the pure `deriveView` function.
 * Never returns `null` — loading and error both map to `AlarmControlView`
 * variants so every caller can rely on `match(view).exhaustive()`.
 *
 * The single `useMemo` is around `deriveView` itself: it's the non-trivial
 * computation (schedule evaluation can walk up to 30 days of rules in
 * `nextMode`) and downstream `useEffect` dependencies read `view` for
 * equality. The surrounding classification is cheap so it runs inline.
 */
export function useAlarmControlView(input: UseAlarmControlViewInput): AlarmControlView {
  const apiConfig = useApiConfig();
  const tick = useMinuteTick();
  const now = input.now ?? tick;

  const { data, isLoading, isError } = useQuery({
    ...alarmConfigurationQuery(apiConfig, {
      siteId: input.siteId,
      assetCameraKey: input.assetCameraKey,
    }),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const pendingIntent = input.pendingIntent ?? null;

  // Depend on `cameraStatus.kind` (a primitive) rather than `cameraStatus`
  // itself: callers naturally construct the object inline (e.g. the connected
  // story passes `{ kind: cameraStatus }`) which would defeat the memo on
  // every parent re-render. `CameraStatus` is `{ kind }` only, so the kind
  // string captures the full value. (`silence` is genuinely structural, so
  // we still depend on the reference; callers should memoise it.)
  const cameraStatusKind = input.cameraStatus.kind;
  return useMemo(() => {
    // Explicit three-state classification so `deriveView` can distinguish
    // "still loading" from "query failed" from "response didn't parse".
    // Previously these were all collapsed into a single `null` configuration
    // which produced a forever-loading spinner on any failure path.
    const source: ConfigurationSource =
      isError ? { kind: "error", reason: "query" }
      : isLoading || data === undefined ? { kind: "loading" }
      : classifyData(data);
    return deriveView({
      source,
      cameraStatus: { kind: cameraStatusKind },
      silence: input.silence,
      now,
      pendingIntent,
    });
  }, [data, isError, isLoading, cameraStatusKind, input.silence, pendingIntent, now]);
}

const classifyData = (data: unknown): ConfigurationSource => {
  // The API wire schema is a permissive passthrough (see
  // `client-messages/alarms.ts`); decode through the domain v3 schema to
  // coerce the shape into a fully-typed `AlarmConfiguration`. A parse
  // failure is a bug in the backend contract — surface it as an error
  // instead of pretending we're still loading.
  const parsed = AlarmConfigurationSchema.safeParse(data);
  if (!parsed.success) return { kind: "error", reason: "schema" };
  return { kind: "ready", configuration: parsed.data };
};
